/* eslint-disable import/extensions */
/* eslint-disable camelcase */
/* eslint-disable no-unused-vars */
import React, {
 useState, useMemo, useContext, useEffect 
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { setSimType } from '@apps/redux/features/SimSelectionSlice'
import { APPLICATION_STATE, ApplicationContext } from '@apps/contexts/ApplicationContext'
import { getById, getBySlug } from '@apps/utils/contentful'
import { setPageAnalytics } from '@apps/utils/Analytics'
import Heading4 from '@apps/components/Typography/Heading4'
import Modal from '@apps/components/Modal'
import { SIM_OPTIONS , ANALYTICS_EVENT_TYPES } from '@apps/utils/constants'
import { Typography } from '@telus/koodo-ui-kit'
import Accordion from '@/apps/components/Accordion'
import GetNewPSimCard from '@/apps/components/GetNewPSimCard'
import ConsumerESIMCompatibility from '@/apps/components/ConsumerESIMCompatibility'
import useTagManagerInfo from '../../../_engine/hooks/useTagManagerInfo'

const HEADING_TOKENS = {
  color: '#000000',
  fontName: 'StagSans',
  fontSize: 24,
  fontWeight: 700,
  letterSpacing: 0,
  lineHeight: '32px'
}
const MODAL_COPY_TOKENS = {
  color: '#000000',
  fontSize: 14,
  fontWeight: 400,
  letterSpacing: 0,
  lineHeight: '24px'
}

const NewESimDetail = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const { pageContent } = useSelector((state) => state.common.content)
  const { simType, cost: simCost } = useSelector((state) => state.simSelectionPage.selectSimType)

  const { 
    [APPLICATION_STATE.CURRENT_LANGUAGE]: language,
    [APPLICATION_STATE.CURRENT_REGION]: region
  } = useContext(ApplicationContext)
  
  const tagManagerInfo = useTagManagerInfo()
  
  const pushClickAnalytics = ({ click_text, event_type, event_click = ANALYTICS_EVENT_TYPES.CLICK}={}) => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(
      setPageAnalytics(location, 'click', region, language, { 
        ...tagManagerInfo, 
        click_text
      }, event_type, event_click )
    )
  }

  const haveSimPage = getById(pageContent, `haveSimPage`)?.[language] || {}
  const { shortTextFields, longTextFields } = haveSimPage

  const textContent = useMemo(() => {
    if (!shortTextFields) return {}
    return {
      chooseASIM: getBySlug(shortTextFields, 'chooseASIM')?.value,
      getNewSIM: getBySlug(shortTextFields, 'getNewSIM')?.value,
      checkAndActivate: getBySlug(shortTextFields, 'checkAndActivate')?.value,
      getNewSIMCard: getBySlug(shortTextFields, 'getNewSIMCard')?.value,
      purchaseAndResumeLater: getBySlug(shortTextFields, 'purchaseAndResumeLater')?.value,
      whatIsESIM: getBySlug(shortTextFields, 'whatIsESIM')?.value,
      eSIMDefinition: getBySlug(shortTextFields, 'eSIMDefinition')?.value,
      activationBenefit: getBySlug(shortTextFields, 'activationBenefit')?.value,
      securityBenefit: getBySlug(shortTextFields, 'securityBenefit')?.value,
      multipleNumbersBenefit: getBySlug(shortTextFields, 'multipleNumbersBenefit')?.value,
      compatibilityWarning: getBySlug(shortTextFields, 'compatibilityWarning')?.value,
      whatIsSIMCard: getBySlug(shortTextFields, 'whatIsSIMCard')?.value,
      simCardDefinition: getBySlug(longTextFields, 'simCardDefinition')?.value,
      eSimCost: getBySlug(shortTextFields, 'eSimCost')?.value,
      pSimCost: getBySlug(shortTextFields, 'pSimCost')?.value
    }
  }, [shortTextFields])

  const eSimPrice = parseFloat(textContent?.eSimCost) ?? 0
  const pSimPrice = parseFloat(textContent?.pSimCost) ?? 0

  const [activeIndex, setActiveIndex] = useState(null)
  const [compatibilityCheckOpen, setCompatibilityCheckOpen] = useState(!!simCost || false)
  const [eSimModalOpen, setESimModalOpen] = useState(false)
  const [pSimModalOpen, setPSimModalOpen] = useState(false)

  const openESIMCompatibilitySection = activeIndex === SIM_OPTIONS.E_SIM && compatibilityCheckOpen

  const toggleActiveIndex = (index) => {
    setActiveIndex(activeIndex === index ? null : index)
  }

  useEffect(() => {
    toggleActiveIndex(simType)
  }, [simType])

  const resetSimSelection = () => {
    setCompatibilityCheckOpen(false)
    dispatch(
      setSimType({
        simType: '',
        cost: 0
      })
    )
  }

  const setSimSelection = (selection, cost) => {
    dispatch(
      setSimType({
        simType: selection,
        cost
      })
    )
  }

  const handleConsumerESIMCompatibility = () => {
    if (activeIndex === SIM_OPTIONS.E_SIM) {
      resetSimSelection()
    } else {
      setCompatibilityCheckOpen(true)
      setSimSelection(SIM_OPTIONS.E_SIM, eSimPrice)
    }
  }

  const handleGetNewPSIM = () => {
    if (activeIndex === SIM_OPTIONS.P_SIM) {
      resetSimSelection()
    } else {
      setSimSelection(SIM_OPTIONS.P_SIM, pSimPrice)
    }
  }

  const handleNewESimInfoClick = () => {
    setESimModalOpen(!eSimModalOpen)
  }

  const handleNewPSimInfoClick = () => {
    setPSimModalOpen(!pSimModalOpen)
  }

  return (
    <div>
      <Heading4 tokens={HEADING_TOKENS} variant={{ size: 24, bold: true, color: '#000000' }}>
        {`${textContent?.chooseASIM} `}
      </Heading4>
      <Accordion
        title={textContent?.getNewSIM}
        subtitle={textContent?.checkAndActivate}
        open={openESIMCompatibilitySection}
        setOpen={handleConsumerESIMCompatibility}
        onInfoClick={handleNewESimInfoClick}
        testId="newsimcard-getanew-sim"
      >
        <ConsumerESIMCompatibility />
      </Accordion>
      <Accordion
        title={textContent?.getNewSIMCard}
        subtitle={textContent?.purchaseAndResumeLater}
        open={activeIndex === SIM_OPTIONS.P_SIM}
        setOpen={handleGetNewPSIM}
        onInfoClick={handleNewPSimInfoClick}
        testId="newsimcard-getanew-psim"
      >
        <GetNewPSimCard />
      </Accordion>
      <Modal
        isOpen={eSimModalOpen}
        closeModal={handleNewESimInfoClick}
        title={textContent?.whatIsESIM}
      >
        <div className="text-black mt-4">
          <Typography
            block
            tag="p"
            variant={{ size: 14, color: '#000000' }}
            tokens={MODAL_COPY_TOKENS}
          >
            {textContent?.eSIMDefinition}
          </Typography>
          <ul className="ml-6 mb-6 list-disc">
            <li>
              <Typography
                variant={{ size: 14, color: '#000000' }}
                tokens={MODAL_COPY_TOKENS}
              >
                {textContent?.activationBenefit}
              </Typography>
            </li>
            <li>
              <Typography
                variant={{ size: 14, color: '#000000' }}
                tokens={MODAL_COPY_TOKENS}
              >
                {textContent?.multipleNumbersBenefit}
              </Typography>
            </li>
            <li>
              <Typography
                variant={{ size: 14, color: '#000000' }}
                tokens={MODAL_COPY_TOKENS}
              >
                {textContent?.securityBenefit}
              </Typography>
            </li>
          </ul>
          <Typography
            block
            tag="p"
            variant={{ size: 14, color: '#000000' }}
            tokens={MODAL_COPY_TOKENS}
          >
            {textContent?.compatibilityWarning}
          </Typography>
        </div>
      </Modal>
      <Modal
        isOpen={pSimModalOpen}
        closeModal={handleNewPSimInfoClick}
        title={textContent?.whatIsSIMCard}
      >
        <div className="text-black mt-4">
          <Typography
            block
            tag="p"
            variant={{ size: 14, color: '#000000' }}
            tokens={MODAL_COPY_TOKENS}
          >
            {textContent?.simCardDefinition}
          </Typography>
        </div>
      </Modal>
    </div>
  )
}

export default NewESimDetail
